import { KeyboardEvent, SyntheticEvent, useState } from 'react';
import { useAppDispatch } from '../../../hooks';
import { forgotPassword } from '../../../store/slices';
import { PrimaryButton, PrimaryInput } from '../../components/shared';
import { useNavigate } from 'react-router-dom';
import { ForgotPasswordParams } from '../../../types';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState<ForgotPasswordParams>({
    email: '',
  });

  const _handleForgot = (e?: SyntheticEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!payload.email) return;

    setLoading(true);
    dispatch(forgotPassword({ payload, callback: () => setSent(true) })).finally(
      () => setLoading(false)
    );
  };

  const _handleResend = () => {
    dispatch(forgotPassword({ payload }));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      _handleForgot();
    }
  };

  const onGetInputValue = (event: SyntheticEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const name = event.currentTarget.name;

    setPayload({ ...payload, [name]: value });
  };

  if (sent) {
    return (
      <div className="h-screen bg-neutral-200 flex items-center justify-center">
        <div className="flex flex-col items-center bg-white rounded-2xl w-full max-w-md p-10 shadow-lg">
          <h1 className="text-neutral-800 bold text-center text-2xl mt-4">
            Verification mail sent
          </h1>
          <p className="text-neutral-500 text-center text-sm mt-2">
            A password reset link has been sent to your mail box.
            <br />
            Please check your email to continue
          </p>

          <p className="text-neutral-500 text-center text-sm mt-8">
            If you did not get the mail please check your spam.
          </p>

          <span className="text-center mt-4">
            <span className="text-neutral-500 text-sm">Didn't receive email?</span>
            {' '}
            <button
              type="button"
              onClick={_handleResend}
              className="text-primary medium focus:outline-none text-sm"
            >
              Resend mail
            </button>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-neutral-200 flex items-center justify-center">
      <form
        className="flex flex-col items-stretch bg-white rounded-2xl w-full max-w-md p-10 shadow-lg"
        onSubmit={_handleForgot}
      >
        <h1 className="text-neutral-800 bold text-center text-2xl mt-4">
          Forgot password
        </h1>
        <p className="text-neutral-500 text-center text-sm mt-2">
          Enter the email address associated with your account
          and we will send you a verification mail
        </p>

        <PrimaryInput
          onChange={onGetInputValue}
          onKeyDown={handleKeyDown}
          placeholder="Enter email"
          name="email"
          label="Email address"
          className="w-full"
        />

        <PrimaryButton
          disabled={!payload.email || loading}
          loading={loading}
          text="Send reset link"
          type="submit"
          classNames="mt-8 medium"
        />

        <span className="text-center mt-5">
          <span className="text-neutral-500 text-sm">Remember password?</span>
          {' '}
          <button
            type="button"
            onClick={() => navigate('/auth/login')}
            className="text-primary medium focus:outline-none text-sm"
          >
            Login
          </button>
        </span>
      </form>
    </div>
  );
};

export default ForgotPassword;
